'use client'

import { useState, useCallback, useEffect } from 'react'
import { Button, Popover, ActionList } from '@shopify/polaris'

export type DashboardExportSegment = 'delivered' | 'returned' | 'cancelled'

type Props = {
  onExport: (segment?: DashboardExportSegment) => void
  loading?: boolean
  disabled?: boolean
  /** Primary = solid CTA; secondary = quieter control */
  variant?: 'primary' | 'secondary'
}

/**
 * Orders CSV export as a single control — audit export plus segment shortcuts
 * (replaces a row of separate export buttons on the dashboard).
 */
export function DashboardOrdersExportMenu({
  onExport,
  loading,
  disabled,
  variant = 'secondary',
}: Props) {
  const [open, setOpen] = useState(false)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (loading) close()
  }, [loading, close])

  const run = useCallback(
    (segment?: DashboardExportSegment) => {
      onExport(segment)
      close()
    },
    [onExport, close]
  )

  return (
    <Popover
      active={open}
      preferredPosition="below"
      autofocusTarget="none"
      activator={
        <Button
          disclosure="down"
          variant={variant}
          loading={loading}
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
        >
          Export orders
        </Button>
      }
      onClose={close}
    >
      <ActionList
        items={[
          {
            content: 'All orders (audit CSV)',
            onAction: () => run(undefined),
          },
          {
            content: 'Delivered only',
            onAction: () => run('delivered'),
          },
          {
            content: 'Returned only',
            onAction: () => run('returned'),
          },
          {
            content: 'Cancelled only',
            onAction: () => run('cancelled'),
          },
        ]}
      />
    </Popover>
  )
}
